<template>
  <div class="ai-chat-surface">
    <div class="_header">
      <ai-chat-history-dropdown
        class="_title"
        :chat-id="chatId"
        @select="$emit('select-chat', $event)"
      />
      <div class="_actions">
        <m-tooltip placement="bottom">
          <template #title>
            {{ $t('aiChat.newChat') }}
          </template>
          <m-btn
            icon="plus"
            light
            hide-border
            fab
            small
            @click="$emit('new-chat')"
          />
        </m-tooltip>
        <ai-chat-chat-menu
          v-if="chatId !== null"
          :chat-id="chatId"
        />
        <slot name="header-actions" />
      </div>
    </div>
    <div class="_body">
      <slot>
        <div class="_placeholder">
          {{ $t('aiChat.emptyState') }}
        </div>
      </slot>
    </div>
  </div>
</template>

<script setup>
import AiChatChatMenu from '@/components/ai-chat/AiChatChatMenu.vue';
import AiChatHistoryDropdown from '@/components/ai-chat/AiChatHistoryDropdown.vue';
import useAiChatRepo from '@/composables/ai-chat/ai-chat-repo';
import { computed, watch } from 'vue';

const props = defineProps({
  chatId: {
    type: Number,
    default: null,
  },
});

const emit = defineEmits(['new-chat', 'select-chat']);

const { queryChat, selectChat } = useAiChatRepo();

const activeChat = computed(() => (props.chatId === null ? null : selectChat(props.chatId)));

watch(() => props.chatId, (chatId) => {
  if (chatId !== null) {
    queryChat(chatId);
  }
}, { immediate: true });

// deleting the open chat drops it from the store before the menu that triggered the
// delete is unmounted, so the surface watches the chat itself rather than the event
watch(activeChat, (chat, previous) => {
  if (chat === undefined && previous?.uid === props.chatId) {
    emit('new-chat');
  }
});
</script>

<style
  scoped
  lang="scss"
  type="text/scss"
>
  .ai-chat-surface {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    min-width: 0;
    overflow: hidden;

    ._header {
      display: flex;
      align-items: center;
      flex-shrink: 0;
      gap: .4rem;
      padding: .8rem 1.2rem;
    }

    ._title {
      min-width: 0;
    }

    ._actions {
      display: flex;
      align-items: center;
      flex-shrink: 0;
      gap: .2rem;
      margin-left: auto;
    }

    ._body {
      flex: 1;
      min-height: 0;
      overflow-y: auto;
    }

    ._placeholder {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100%;
      padding: 2rem;
      text-align: center;
      color: $font-color-secondary;
    }
  }
</style>
